import { useEffect, useRef, useState, type ReactNode } from "react";
import { usePathname, useRouter } from "expo-router";
import {
  Alert,
  Modal,
  Platform,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from "react-native";

import { sidebarInset, makeStylesheet } from "~/components/AppShell.styles";
import {
  AppText,
  BrandMark,
  Button,
  ButtonText,
  Icon,
  Stack,
} from "~/components/ui";
import { useAuth, type AuthUser } from "~/hooks/useAuth";
import { useSubscription } from "~/hooks/useSubscription";
import {
  useWorkspaceSwitcher,
  useWorkspaceTimeEntries,
} from "~/hooks/workspace";
import { useNotifications } from "~/hooks/useNotifications";
import { palette, spacing, useTheme, useThemedStyles } from "~/styles/theme";
import { openExternalUrl } from "~/utils/external-link";
import { getExternalLinks } from "~/utils/runtime-config";
import {
  hasEverSubscribed,
  hasPaidAccess,
  SUBSCRIPTION_PATH,
} from "~/utils/onboarding";

const primaryNavItems = [
  { href: "/dashboard", label: "Dashboard" },
  { href: "/timer", label: "Timer" },
  { href: "/time-entries", label: "Time" },
  { href: "/clients", label: "Clients" },
  { href: "/projects", label: "Projects" },
  { href: "/quotes", label: "Quotes" },
  { href: "/invoices", label: "Invoices" },
  { href: "/expenses", label: "Expenses" },
] as const;

const moreNavItems = [
  { href: "/calendar", label: "Calendar" },
  { href: "/tasks", label: "Tasks" },
  { href: "/automations", label: "Automations" },
  { href: "/mileage", label: "Mileage" },
  { href: "/assets", label: "Assets" },
  { href: "/reports", label: "Reports" },
] as const;

function formatActiveTimer(startedAt: string, now: number) {
  const seconds = Math.max(
    0,
    Math.floor((now - new Date(startedAt).getTime()) / 1000),
  );
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainder = seconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`;
}

function parentRoute(pathname: string) {
  if (
    /^\/(clients|projects|tasks|invoices|time-entries|expenses)\/[^/]+$/.test(
      pathname,
    )
  ) {
    return pathname.replace(/\/[^/]+$/, "");
  }
  return null;
}

function userInitials(user: AuthUser) {
  const initials = [user.firstName, user.lastName]
    .filter(Boolean)
    .map((part) => part.trim().charAt(0).toUpperCase())
    .join("");

  return initials || user.email.charAt(0).toUpperCase();
}

function Navigation({
  user,
  logoutPending,
  unreadCount,
  workspaceCount,
  workspaceName,
  onLogoutRequest,
  onNavigate,
  onWorkspaceRequest,
}: {
  user: AuthUser | null;
  logoutPending: boolean;
  unreadCount: number;
  workspaceCount: number;
  workspaceName?: string;
  onLogoutRequest: () => void;
  onNavigate?: () => void;
  onWorkspaceRequest: () => void;
}) {
  const pathname = usePathname();
  const router = useRouter();
  const externalLinks = getExternalLinks();
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStylesheet);

  return (
    <View style={stylesheet.sidebarBody}>
      <View style={stylesheet.sidebarBrand}>
        <BrandMark size="md" tone="inverse" />
      </View>
      {workspaceName ? (
        <Pressable
          accessibilityRole={workspaceCount > 1 ? "button" : "text"}
          accessibilityLabel={
            workspaceCount > 1
              ? `Switch workspace. Current workspace: ${workspaceName}`
              : `Current workspace: ${workspaceName}`
          }
          disabled={workspaceCount < 2}
          onPress={onWorkspaceRequest}
          style={({ hovered, pressed }) => [
            stylesheet.workspaceTrigger,
            workspaceCount > 1 && (hovered || pressed)
              ? stylesheet.navItemHover
              : null,
          ]}
        >
          <View style={stylesheet.workspaceIcon}>
            <Icon name="building-2" size={15} color={palette.brandDark} />
          </View>
          <View style={stylesheet.workspaceTriggerCopy}>
            <Text numberOfLines={1} style={stylesheet.workspaceName}>
              {workspaceName}
            </Text>
            <Text style={stylesheet.workspaceHint}>
              {workspaceCount > 1 ? "Switch workspace" : "Your workspace"}
            </Text>
          </View>
          {workspaceCount > 1 ? (
            <Icon name="chevrons-up-down" size={15} color={colors.background} />
          ) : null}
        </Pressable>
      ) : null}
      <Stack gap="xs">
        {primaryNavItems.map((item) => {
          const active =
            pathname === item.href || pathname.startsWith(`${item.href}/`);
          return (
            <Pressable
              key={item.href}
              accessibilityRole="link"
              accessibilityState={{ selected: active }}
              onPress={() => {
                router.push(item.href as never);
                onNavigate?.();
              }}
              style={({ hovered, pressed }) => [
                stylesheet.navItem,
                active ? stylesheet.navItemActive : null,
                hovered || pressed ? stylesheet.navItemHover : null,
              ]}
            >
              <Text
                style={[
                  stylesheet.navItemText,
                  active ? stylesheet.navItemTextActive : null,
                ]}
              >
                {item.label}
              </Text>
            </Pressable>
          );
        })}
        <Text style={stylesheet.navSectionLabel}>More</Text>
        {moreNavItems.map((item) => {
          const active =
            pathname === item.href || pathname.startsWith(`${item.href}/`);
          return (
            <Pressable
              key={item.href}
              accessibilityRole="link"
              accessibilityState={{ selected: active }}
              onPress={() => {
                router.push(item.href as never);
                onNavigate?.();
              }}
              style={({ hovered, pressed }) => [
                stylesheet.navItem,
                active ? stylesheet.navItemActive : null,
                hovered || pressed ? stylesheet.navItemHover : null,
              ]}
            >
              <Text
                style={[
                  stylesheet.navItemText,
                  active ? stylesheet.navItemTextActive : null,
                ]}
              >
                {item.label}
              </Text>
            </Pressable>
          );
        })}
        <Pressable
          accessibilityRole="link"
          accessibilityState={{ selected: pathname === "/team" }}
          onPress={() => {
            router.push("/team" as never);
            onNavigate?.();
          }}
          style={({ hovered, pressed }) => [
            stylesheet.sidebarUtilityLink,
            pathname === "/team" ? stylesheet.navItemActive : null,
            hovered || pressed ? stylesheet.navItemHover : null,
          ]}
        >
          <Text style={stylesheet.sidebarUtilityText}>Seats & accountant</Text>
        </Pressable>
        <Pressable
          accessibilityRole="link"
          accessibilityState={{
            selected: pathname.startsWith("/notifications"),
          }}
          onPress={() => {
            router.push("/notifications" as never);
            onNavigate?.();
          }}
          style={({ hovered, pressed }) => [
            stylesheet.sidebarUtilityLink,
            pathname.startsWith("/notifications")
              ? stylesheet.navItemActive
              : null,
            hovered || pressed ? stylesheet.navItemHover : null,
          ]}
        >
          <View style={stylesheet.utilityContent}>
            <Text style={stylesheet.sidebarUtilityText}>Notifications</Text>
            {unreadCount > 0 ? (
              <View style={stylesheet.notificationBadge}>
                <Text style={stylesheet.notificationBadgeText}>
                  {unreadCount}
                </Text>
              </View>
            ) : null}
          </View>
        </Pressable>
        <View style={stylesheet.sidebarDivider} />
        <Pressable
          accessibilityRole="link"
          accessibilityState={{ selected: pathname === "/settings" }}
          onPress={() => {
            router.push("/settings" as never);
            onNavigate?.();
          }}
          style={({ hovered, pressed }) => [
            stylesheet.sidebarUtilityLink,
            pathname === "/settings" ? stylesheet.navItemActive : null,
            hovered || pressed ? stylesheet.navItemHover : null,
          ]}
        >
          <Text style={stylesheet.sidebarUtilityText}>Settings</Text>
        </Pressable>
        {externalLinks.help ? (
          <Pressable
            accessibilityRole="link"
            onPress={() => void openExternalUrl(externalLinks.help)}
            style={({ hovered, pressed }) => [
              stylesheet.sidebarUtilityLink,
              hovered || pressed ? stylesheet.navItemHover : null,
            ]}
          >
            <Text style={stylesheet.sidebarUtilityText}>Help</Text>
          </Pressable>
        ) : null}
        <Pressable
          accessibilityRole="link"
          accessibilityState={{ selected: pathname === "/subscription" }}
          onPress={() => {
            router.push("/subscription" as never);
            onNavigate?.();
          }}
          style={({ hovered, pressed }) => [
            stylesheet.sidebarUtilityLink,
            pathname === "/subscription" ? stylesheet.navItemActive : null,
            hovered || pressed ? stylesheet.navItemHover : null,
          ]}
        >
          <Text style={stylesheet.sidebarUtilityText}>Subscription</Text>
        </Pressable>
        <Button
          variant="link"
          align="start"
          iconName="log-out"
          onPress={onLogoutRequest}
          disabled={logoutPending}
        >
          <ButtonText>
            {logoutPending ? "Signing out..." : "Sign out"}
          </ButtonText>
        </Button>
      </Stack>

      <View style={{ flex: 1 }} />
      <View style={stylesheet.sidebarFooter}>
        {user ? (
          <Pressable
            accessibilityRole="link"
            accessibilityLabel="Open profile settings"
            onPress={() => {
              router.push("/settings" as never);
              onNavigate?.();
            }}
            style={({ hovered, pressed }) => [
              stylesheet.sidebarProfile,
              hovered || pressed ? stylesheet.navItemHover : null,
            ]}
          >
            <View style={stylesheet.sidebarAvatar}>
              <Text style={stylesheet.sidebarAvatarText}>
                {userInitials(user)}
              </Text>
            </View>
            <Text numberOfLines={1} style={stylesheet.sidebarProfileName}>
              {[user.firstName, user.lastName].filter(Boolean).join(" ") ||
                user.email}
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}

function titleCase(value: string) {
  return value
    .replace(/-/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const UUID_SEGMENT =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The last readable path segment: a trailing id (collections/<uuid>) isn't a
 * title, so name the section instead of showing "4a29d889 84e1 4817…". */
function lastReadableSegment(pathname: string) {
  const segments = pathname.split("/").filter(Boolean);
  while (segments.length > 1 && UUID_SEGMENT.test(segments.at(-1) ?? "")) {
    segments.pop();
  }
  return segments.at(-1);
}

function breadcrumbsFor(pathname: string) {
  if (pathname === "/dashboard") return [{ label: "Dashboard" }];
  if (pathname === "/settings") return [{ label: "Settings" }];
  const label = lastReadableSegment(pathname);
  return [{ label: label || "MintStint" }];
}

function pageTitleFor(pathname: string) {
  const breadcrumbs = breadcrumbsFor(pathname);
  return `${breadcrumbs.at(-1)?.label ?? "MintStint"} | MintStint`;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { colors } = useTheme();
  const stylesheet = useThemedStyles(makeStylesheet);
  const { width } = useWindowDimensions();
  const pathname = usePathname();
  const router = useRouter();
  const { user, logout, logoutPending } = useAuth();
  const subscription = useSubscription();
  const { unreadCount } = useNotifications();
  const workspaceSwitcher = useWorkspaceSwitcher();
  const timeEntries = useWorkspaceTimeEntries(
    workspaceSwitcher.selectedWorkspace?.id,
  );
  const desktop = width >= 768;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [workspaceSwitchOpen, setWorkspaceSwitchOpen] = useState(false);
  const [timerNow, setTimerNow] = useState(Date.now());
  const backRoute = Platform.OS === "web" ? null : parentRoute(pathname);
  const breadcrumbs = breadcrumbsFor(pathname);
  const showExpiredBanner =
    Boolean(user) &&
    hasEverSubscribed(user) &&
    !hasPaidAccess(user) &&
    subscription.isExpired;
  const activeTimer = (timeEntries.data ?? []).find((entry) => !entry.ended_at);
  const idleNudgeMinutes =
    workspaceSwitcher.selectedWorkspace?.idle_nudge_minutes ?? 0;
  const focusReminderMinutes =
    workspaceSwitcher.selectedWorkspace?.focus_reminder_minutes ?? 0;
  const lastActivityRef = useRef(Date.now());
  const idleAlertOpenRef = useRef(false);
  const focusStartedRef = useRef<number | null>(null);

  useEffect(() => {
    if (!activeTimer) return;
    const interval = setInterval(() => setTimerNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [activeTimer?.id]);

  useEffect(() => {
    if (!activeTimer) {
      idleAlertOpenRef.current = false;
      focusStartedRef.current = null;
      return;
    }
    lastActivityRef.current = Date.now();
    focusStartedRef.current = Date.now();
    idleAlertOpenRef.current = false;

    const markActivity = () => {
      lastActivityRef.current = Date.now();
      idleAlertOpenRef.current = false;
    };

    const listeners: Array<[string, EventListener]> = [];
    if (Platform.OS === "web" && typeof window !== "undefined") {
      for (const eventName of [
        "mousemove",
        "keydown",
        "touchstart",
        "visibilitychange",
      ]) {
        const handler: EventListener = () => {
          if (
            eventName === "visibilitychange" &&
            typeof document !== "undefined" &&
            document.visibilityState === "hidden"
          ) {
            return;
          }
          markActivity();
        };
        window.addEventListener(eventName, handler);
        listeners.push([eventName, handler]);
      }
    }

    const tick = setInterval(() => {
      if (!activeTimer) return;
      const now = Date.now();
      if (
        idleNudgeMinutes > 0 &&
        !idleAlertOpenRef.current &&
        now - lastActivityRef.current >= idleNudgeMinutes * 60_000
      ) {
        idleAlertOpenRef.current = true;
        Alert.alert("Still tracking?", "Your timer is still running.", [
          {
            text: "Keep going",
            onPress: () => {
              lastActivityRef.current = Date.now();
              idleAlertOpenRef.current = false;
            },
          },
          {
            text: "Stop timer",
            style: "destructive",
            onPress: () => {
              void timeEntries.stopTimerEntry.mutateAsync({
                id: activeTimer.id,
                endedAt: new Date(),
              });
            },
          },
        ]);
      }

      if (
        focusReminderMinutes > 0 &&
        focusStartedRef.current &&
        now - focusStartedRef.current >= focusReminderMinutes * 60_000
      ) {
        focusStartedRef.current = now;
        Alert.alert("Focus check-in", "Focus check-in — timer still running");
      }
    }, 15_000);

    return () => {
      clearInterval(tick);
      for (const [eventName, handler] of listeners) {
        window.removeEventListener(eventName, handler);
      }
    };
  }, [
    activeTimer?.id,
    focusReminderMinutes,
    idleNudgeMinutes,
    timeEntries.stopTimerEntry,
  ]);

  useEffect(() => {
    if (Platform.OS !== "web" || typeof document === "undefined") return;
    document.title = pageTitleFor(pathname);
  }, [pathname]);

  return (
    <View style={stylesheet.appShell}>
      {desktop ? (
        <View style={stylesheet.sidebar}>
          <Navigation
            user={user}
            logoutPending={logoutPending}
            unreadCount={unreadCount}
            workspaceCount={workspaceSwitcher.workspaces.length}
            workspaceName={workspaceSwitcher.selectedWorkspace?.name}
            onLogoutRequest={() => setLogoutConfirmOpen(true)}
            onWorkspaceRequest={() => setWorkspaceSwitchOpen(true)}
          />
        </View>
      ) : null}

      <View style={stylesheet.appShellContent}>
        {showExpiredBanner ? (
          <View style={stylesheet.expiredBanner}>
            <AppText size="xs" tone="inverse">
              Your subscription has expired. You can still view your data in
              read-only mode. Reactivate to keep working.
            </AppText>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel="Manage subscription"
              onPress={() => router.push(SUBSCRIPTION_PATH as never)}
            >
              <AppText size="xs" weight="semibold" tone="inverse">
                Manage subscription →
              </AppText>
            </Pressable>
          </View>
        ) : null}
        <View style={stylesheet.shellHeader}>
          <View style={stylesheet.shellHeaderLeft}>
            {!desktop ? (
              <Button
                variant="outline"
                compact
                onPress={() => setDrawerOpen(true)}
              >
                <ButtonText>Menu</ButtonText>
              </Button>
            ) : null}
            {backRoute ? (
              <Button
                variant="outline"
                compact
                onPress={() => router.replace(backRoute as never)}
              >
                <ButtonText>Back</ButtonText>
              </Button>
            ) : null}
            {!desktop ? <BrandMark size="sm" /> : null}
            <View style={stylesheet.breadcrumbs}>
              {breadcrumbs.map((breadcrumb, index) => (
                <View
                  key={`${breadcrumb.label}-${index}`}
                  style={stylesheet.breadcrumbItem}
                >
                  {index > 0 ? (
                    <Text style={stylesheet.breadcrumbSeparator}>{" / "}</Text>
                  ) : null}
                  <Text numberOfLines={1} style={stylesheet.breadcrumbCurrent}>
                    {titleCase(breadcrumb.label)}
                  </Text>
                </View>
              ))}
            </View>
          </View>
          <View style={stylesheet.shellActions}>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel={
                activeTimer ? "Open running timer" : "Open timer"
              }
              onPress={() => router.push("/timer" as never)}
              style={({ hovered, pressed }) => [
                stylesheet.timerQuickAction,
                hovered || pressed ? stylesheet.headerIconButtonHover : null,
              ]}
            >
              <Icon
                name={activeTimer ? "timer" : "play"}
                size={16}
                color={colors.heading}
              />
              <Text style={stylesheet.timerQuickActionText}>
                {activeTimer
                  ? formatActiveTimer(activeTimer.started_at, timerNow)
                  : "Timer"}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="link"
              accessibilityLabel={`Notifications${unreadCount ? `, ${unreadCount} unread` : ""}`}
              onPress={() => router.push("/notifications" as never)}
              style={({ hovered, pressed }) => [
                stylesheet.headerIconButton,
                hovered || pressed ? stylesheet.headerIconButtonHover : null,
              ]}
            >
              <Icon name="bell" size={18} color={colors.heading} />
              {unreadCount > 0 ? <View style={stylesheet.headerBadge} /> : null}
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Support"
              onPress={() => router.push("/support" as never)}
              style={({ hovered, pressed }) => [
                stylesheet.headerIconButton,
                hovered || pressed ? stylesheet.headerIconButtonHover : null,
              ]}
            >
              <Icon name="megaphone" size={18} color={colors.heading} />
            </Pressable>
          </View>
        </View>
        {children}
      </View>

      <Modal
        animationType="slide"
        transparent
        visible={drawerOpen}
        onRequestClose={() => setDrawerOpen(false)}
      >
        <View style={stylesheet.drawerOverlay}>
          <View style={stylesheet.drawerPanel}>
            <Navigation
              user={user}
              logoutPending={logoutPending}
              unreadCount={unreadCount}
              workspaceCount={workspaceSwitcher.workspaces.length}
              workspaceName={workspaceSwitcher.selectedWorkspace?.name}
              onLogoutRequest={() => {
                setDrawerOpen(false);
                setLogoutConfirmOpen(true);
              }}
              onNavigate={() => setDrawerOpen(false)}
              onWorkspaceRequest={() => {
                setDrawerOpen(false);
                setWorkspaceSwitchOpen(true);
              }}
            />
          </View>
          <Pressable
            accessibilityLabel="Close navigation"
            style={stylesheet.drawerDismiss}
            onPress={() => setDrawerOpen(false)}
          />
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={workspaceSwitchOpen}
        onRequestClose={() => setWorkspaceSwitchOpen(false)}
      >
        <View style={stylesheet.modalOverlay}>
          <View style={stylesheet.workspacePickerCard}>
            <View style={stylesheet.workspacePickerHeader}>
              <View style={stylesheet.workspacePickerHeading}>
                <Text style={stylesheet.modalTitle}>Switch workspace</Text>
                <AppText size="xs">
                  Choose which business workspace MintStint should open.
                </AppText>
              </View>
              <Button
                variant="outline"
                compact
                onPress={() => setWorkspaceSwitchOpen(false)}
                disabled={workspaceSwitcher.selectWorkspace.isPending}
              >
                <ButtonText>Close</ButtonText>
              </Button>
            </View>
            <View style={stylesheet.workspaceOptions}>
              {workspaceSwitcher.workspaces.map((workspace) => {
                const selected =
                  workspace.id === workspaceSwitcher.selectedWorkspace?.id;
                return (
                  <Pressable
                    key={workspace.id}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    disabled={workspaceSwitcher.selectWorkspace.isPending}
                    onPress={() => {
                      if (selected) {
                        setWorkspaceSwitchOpen(false);
                        return;
                      }
                      workspaceSwitcher.selectWorkspace.mutate(workspace.id, {
                        onSuccess: () => {
                          setWorkspaceSwitchOpen(false);
                          router.replace("/dashboard" as never);
                        },
                      });
                    }}
                    style={({ hovered, pressed }) => [
                      stylesheet.workspaceOption,
                      selected ? stylesheet.workspaceOptionSelected : null,
                      hovered || pressed
                        ? stylesheet.workspaceOptionHover
                        : null,
                    ]}
                  >
                    <View style={stylesheet.workspaceOptionIcon}>
                      <Icon
                        name={selected ? "circle-check" : "building-2"}
                        size={18}
                        color={selected ? palette.brandForest : colors.heading}
                      />
                    </View>
                    <View style={stylesheet.workspaceOptionCopy}>
                      <Text
                        numberOfLines={1}
                        style={stylesheet.workspaceOptionName}
                      >
                        {workspace.name}
                      </Text>
                      <AppText size="xs">
                        {workspace.owner_id === user?.id
                          ? "Owned by you"
                          : "Shared with you"}
                      </AppText>
                    </View>
                  </Pressable>
                );
              })}
            </View>
            {workspaceSwitcher.error ? (
              <AppText size="xs" tone="danger">
                {workspaceSwitcher.error instanceof Error
                  ? workspaceSwitcher.error.message
                  : "Unable to switch workspace."}
              </AppText>
            ) : null}
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent
        visible={logoutConfirmOpen}
        onRequestClose={() => setLogoutConfirmOpen(false)}
      >
        <View style={stylesheet.modalOverlay}>
          <View style={stylesheet.confirmationCard}>
            <View>
              <Text style={stylesheet.modalTitle}>Sign out?</Text>
              <AppText>
                Are you sure you want to log out of your MintStint account?
              </AppText>
            </View>
            <View style={stylesheet.confirmationActions}>
              <Button
                variant="outline"
                compact
                onPress={() => setLogoutConfirmOpen(false)}
                disabled={logoutPending}
              >
                <ButtonText>Cancel</ButtonText>
              </Button>
              <Button
                compact
                onPress={() => void logout()}
                disabled={logoutPending}
              >
                <ButtonText>
                  {logoutPending ? "Signing out..." : "Sign out"}
                </ButtonText>
              </Button>
            </View>
          </View>
        </View>
      </Modal>

    </View>
  );
}
